import { useEffect, useState } from 'react'
import { Alert, Button, Form, Input, InputNumber, Select, Spin } from 'antd'
import { PlatformType } from '@/types/authorization'
import type { SignupPricingForm, PricingOption } from '../../types'
import { couponDetail, findCoupon, getPriceList, validateCoupon } from '../../api'
import { AUTH_PRIMARY_BUTTON_CLASS } from '../../constants'
import { defaultPricingOptions } from '../utils'
import { useSignup } from '../SignupContext'

function shouldShowCustomUsers(option?: PricingOption) {
  if (!option) return false
  const optionText = `${option.label} ${option.value}`.toLowerCase()
  return optionText.includes('>100') || optionText.includes('100+') || optionText.includes('bundle')
}

function toNumericPrice(price?: string) {
  if (price === undefined || price === null) return null
  const normalizedPrice = String(price).trim()
  if (!normalizedPrice || normalizedPrice.toLowerCase() === 'contact') return null
  const parsed = Number(normalizedPrice)
  return Number.isFinite(parsed) ? parsed : null
}

function calculateDisplayedPrice(selectedOption: PricingOption | undefined, customUsers: number, options: PricingOption[]) {
  if (!selectedOption) return ''

  const optionText = `${selectedOption.label} ${selectedOption.value}`.toLowerCase()
  const selectedPrice = toNumericPrice(selectedOption.price)
  if (!optionText.includes('bundle') && !optionText.includes('>100') && !optionText.includes('100+')) {
    return selectedPrice === null ? 'Contact us for pricing' : `$${selectedPrice}`
  }

  if (customUsers <= 100 || selectedPrice === null) return ''

  const nonBundlePrices = options
    .filter((option) => option.value !== selectedOption.value)
    .map((option) => toNumericPrice(option.price))
    .filter((price): price is number => price !== null)

  const maxPrice = nonBundlePrices.length ? Math.max(...nonBundlePrices) : 0
  const bundleMultiplier = Math.ceil((customUsers - 100) / 10)
  return `$${maxPrice + selectedPrice * bundleMultiplier}`
}

export default function PricingStepForm() {
  const {
    pricingInfoForm,
    pricingForm,
    setPricingForm,
    setCouponValid,
    setTrialDuration,
    setActiveUserOptions,
    setStep,
    runAction,
    productSelection,
    isBasSignup,
    isChildClient,
    loading,
    hideBackButton,
    activeUserOptions,
    couponValid,
    trialDuration,
  } = useSignup()
  const [isLoadingPriceList, setIsLoadingPriceList] = useState(false)
  const [loadedPricingProduct, setLoadedPricingProduct] = useState<number | null>(null)

  const selectedActiveUserValue = Form.useWatch('activeUsers', pricingInfoForm) || pricingForm.activeUsers
  const customUsersValue = Number(Form.useWatch('customUsers', pricingInfoForm) ?? pricingForm.customUsers ?? 0)
  const selectedPricingOption = activeUserOptions.find((option) => option.value === selectedActiveUserValue)
  const showActiveUserFields = !isBasSignup && productSelection?.product !== PlatformType.Xero
  const showCustomUsersField = showActiveUserFields && shouldShowCustomUsers(selectedPricingOption)
  const hasSelectedPricingOption = Boolean(selectedActiveUserValue && selectedPricingOption)
  const selectedPlanPrice = hasSelectedPricingOption
    ? calculateDisplayedPrice(selectedPricingOption, customUsersValue, activeUserOptions)
    : ''

  useEffect(() => {
    if (!showActiveUserFields || !productSelection?.product) return
    if (loadedPricingProduct === productSelection.product && activeUserOptions.length > 0) return
    const loadPriceList = async () => {
      setIsLoadingPriceList(true)
      try {
        const list = await getPriceList(productSelection.product)
        setActiveUserOptions(list.length ? list : defaultPricingOptions)
        setLoadedPricingProduct(productSelection.product)
      } catch {
        setActiveUserOptions(defaultPricingOptions)
        setLoadedPricingProduct(productSelection.product)
      } finally {
        setIsLoadingPriceList(false)
      }
    }
    void loadPriceList()
  }, [activeUserOptions.length, loadedPricingProduct, productSelection?.product, setActiveUserOptions, showActiveUserFields])

  useEffect(() => {
    pricingInfoForm.setFieldsValue(pricingForm)
  }, [pricingForm, pricingInfoForm])

  useEffect(() => {
    if (!showCustomUsersField) {
      pricingInfoForm.setFieldValue('customUsers', 0)
    }
  }, [pricingInfoForm, showCustomUsersField])

  const handleNext = async () => {
    await runAction(async () => {
      const values = await pricingInfoForm.validateFields()
      const normalizedPricing: SignupPricingForm = {
        couponCode: values.couponCode?.trim() || '',
        activeUsers: showActiveUserFields ? values.activeUsers || '' : '',
        customUsers: showCustomUsersField ? Number(values.customUsers || 0) : 0,
      }

      if (normalizedPricing.couponCode) {
        const coupons = await validateCoupon({
          Code: normalizedPricing.couponCode,
          PlatformType: productSelection?.product || PlatformType.Xero,
        })
        const validCoupon = findCoupon(coupons, normalizedPricing.couponCode)
        if (!validCoupon) {
          setCouponValid(false)
          setTrialDuration(null)
          throw new Error('You have entered an invalid Promo Code')
        }
        setCouponValid(true)
        setTrialDuration(couponDetail(validCoupon)?.duration_in_months || null)
      } else {
        setCouponValid(false)
        setTrialDuration(null)
      }

      setPricingForm(normalizedPricing)
      if (isChildClient) setStep('login')
      else setStep('payment')
    })
  }

  const handleBack = () => {
    const values = pricingInfoForm.getFieldsValue()
    setPricingForm({
      couponCode: values.couponCode?.trim() || '',
      activeUsers: showActiveUserFields ? values.activeUsers || '' : '',
      customUsers: showCustomUsersField ? Number(values.customUsers || 0) : 0,
    })
    setStep('info')
  }

  return (
    <>
      <Form form={pricingInfoForm} layout="vertical" initialValues={pricingForm}>
        {isBasSignup && (
          <Alert
            type="success"
            showIcon
            message="You are subscribed to free BAS Services till end of 2025"
            className="mb-3"
          />
        )}

        <Form.Item name="couponCode" label="Promo Code">
          <Input placeholder="Please enter a coupon code if available" />
        </Form.Item>

        {showActiveUserFields && (
          <>
            <Form.Item name="activeUsers" label="Active users" rules={[{ required: true, message: 'Select active users' }]}>
              <Select
                loading={isLoadingPriceList}
                disabled={isLoadingPriceList}
                placeholder={isLoadingPriceList ? 'Loading pricing options...' : 'Select active users'}
                options={activeUserOptions.map((o: PricingOption) => ({ value: o.value, label: o.label }))}
              />
            </Form.Item>
            {isLoadingPriceList && (
              <div className="mb-3 flex items-center gap-2 text-sm text-slate-600">
                <Spin size="small" />
                <span>Loading pricing options...</span>
              </div>
            )}
          </>
        )}

        {showCustomUsersField && (
          <Form.Item
            name="customUsers"
            label="Custom users"
            initialValue={0}
            rules={[
              {
                validator: (_, value) => {
                  const numericValue = Number(value || 0)
                  return numericValue > 100
                    ? Promise.resolve()
                    : Promise.reject(new Error('Kindly enter a number greater than 100'))
                },
              },
            ]}
          >
            <InputNumber className="w-full" min={0} />
          </Form.Item>
        )}

        {selectedPlanPrice && (
          <div className="mb-3 rounded-md border border-sky-200 bg-sky-50 px-4 py-3 text-sm text-slate-800">
            <div className="font-medium">1 month WFM Dashboards Subscription</div>
            <ul className="mb-0 mt-2 list-disc pl-5">
              {couponValid && trialDuration && (
                <li>{trialDuration}-month{trialDuration > 1 ? 's' : ''} trial for WFM Clients</li>
              )}
              <li><strong>{selectedPlanPrice}</strong> per month</li>
              <li>Price in AUD and includes GST as applicable</li>
            </ul>
          </div>
        )}

        {couponValid && trialDuration && !selectedPlanPrice && (
          <Alert type="success" showIcon message={`Coupon has been applied (${trialDuration} month trial)`} />
        )}
      </Form>
      <div className="signup-step-actions">
        {!hideBackButton && <Button onClick={handleBack} disabled={loading}>Back</Button>}
        <Button
          type="primary"
          loading={loading}
          disabled={loading || isLoadingPriceList}
          className={AUTH_PRIMARY_BUTTON_CLASS}
          onClick={() => void handleNext()}
        >
          Next
        </Button>
      </div>
    </>
  )
}
